import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { findStrategy } from "../../component/Methods";
import {
  resolveFundAbbreviation,
  fundAbbreviationsMatch,
} from "./utils/portfolioFundUtils";

export { resolveFundAbbreviation, fundAbbreviationsMatch };

export const usePortfolioFilters = ({
  funds,
  strategies,
  isStrategist,
  fundname,
  strategyname,
  fund,
  defaultFund = "ALL",
}) => {
  const [fundName, setFundName] = useState([]);
  const [fundVal, setFundVal] = useState(() =>
    fundname ? fundname : defaultFund
  );
  const [StrategyName, setStrategyName] = useState([{ name: "ALL" }]);
  const [StrategeyVal, setStrategyVal] = useState("");
  const [StrategyType, setStrategyType] = useState(["Both"]);
  const [StrategeyTypename, setStrategyTypename] = useState("Both");
  const [disableStrategyButton, setDisableStrategyButton] = useState(true);
  const defaultFundAppliedRef = useRef(false);

  // Initialize fund names
  useEffect(() => {
    if (funds) {
      const filteredName = funds?.map((i) => i.abbreviation);
      filteredName.unshift("ALL");
      setFundName(filteredName);
    }
  }, [funds]);

  // Helper function to get unique strategy names
  const unique = useCallback((data) => {
    const localUniqueNames = [];
    const localUniqueNamesSet = new Set();

    data.forEach((item) => {
      const nameParts = item.name.split("_");
      const baseName = nameParts[0];
      if (!localUniqueNamesSet.has(baseName)) {
        const checkType = nameParts.length > 1 ? nameParts[1] : "noLS";
        localUniqueNames.push({ name: baseName, checkType });
        localUniqueNamesSet.add(baseName);
      }
    });
    localUniqueNames.push({ name: "ALL" });
    setStrategyName(localUniqueNames);
  }, []);

  // Fund filter logic
  const setFundFilter = useCallback(
    (newValue) => {
      const resolved = resolveFundAbbreviation(funds, newValue);
      const fundObj = funds?.find((i) => i.abbreviation === resolved);

      setStrategyTypename("Both");
      if (resolved === "ALL") {
        setDisableStrategyButton(true);
        setStrategyVal("");
        setFundVal(resolved);
        setStrategyName([{ name: "ALL" }]);
      } else {
        setFundVal(resolved);
        setDisableStrategyButton(false);
        setStrategyVal("");
        const foundFund = fund?.find((row) => row?.fund === fundObj?.fundId);

        if (foundFund) {
          const strategyAUMsData = Object.keys(foundFund?.strategyAUMs || {})
            .filter((strategyId) => strategies.some((strategy) => strategy?.strategyId === strategyId))
            .map((strategyId) => ({
              strategyId,
              name: findStrategy(strategyId, strategies),
              aum: foundFund?.strategyAUMs[strategyId],
            }));

          if (strategyAUMsData?.length > 0) {
            unique(strategyAUMsData);
          } else {
            setStrategyName([]);
          }
        } else {
          setStrategyName([]);
        }
      }
    },
    [funds, fund, strategies, unique]
  );

  // Initialize filters once funds (and accounting fund rows) are available
  useEffect(() => {
    if (isStrategist) {
      setStrategyName(strategies.map((strategy) => ({ name: strategy.name })));
      return;
    }

    if (fundname) {
      setFundFilter(fundname);
    } else if (
      !defaultFundAppliedRef.current &&
      defaultFund !== "ALL" &&
      funds?.length
    ) {
      const resolved = resolveFundAbbreviation(funds, defaultFund);
      if (funds.some((f) => f.abbreviation === resolved)) {
        defaultFundAppliedRef.current = true;
        setFundFilter(resolved);
      }
    }

    if (strategyname) {
      setStrategyVal(strategyname);
    }
  }, [
    isStrategist,
    strategies,
    fundname,
    strategyname,
    defaultFund,
    funds,
    fund,
    setFundFilter,
  ]);

  // Memoized options arrays
  const fundOptions = useMemo(() => fundName?.map((val) => val)?.sort(), [fundName]);

  const strategyOptions = useMemo(
    () =>
      StrategyName?.map((val) => val?.name)?.sort((a, b) => {
        if (a === "ALL") return -1;
        if (b === "ALL") return 1;
        return a.localeCompare(b, undefined, { sensitivity: "base" });
      }),
    [StrategyName]
  );

  return {
    fundName,
    fundVal,
    StrategyName,
    StrategeyVal,
    StrategyType,
    StrategeyTypename,
    disableStrategyButton,
    fundOptions,
    strategyOptions,
    setFundFilter,
    setStrategyVal,
    setStrategyTypename,
    setStrategyType,
  };
};
